import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { generateUserId } from '../utils/idGenerator';
import { ADMIN_EMAILS } from '../constants/admin';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);

  // 🔹 SIGNUP
    async function signup(email, password, additionalData = {}) {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

        // Update Firebase Auth profile (best-effort)
        try {
          if (additionalData.firstName || additionalData.lastName) {
            await updateProfile(user, {
              displayName: `${additionalData.firstName || ''} ${additionalData.lastName || ''}`.trim()
            });
          }
        } catch (profileError) {
          console.warn('Profile update failed:', profileError);
        }

        // Create Firestore user document with custom ID (best-effort)
        try {
          const customUserId = generateUserId();
          await setDoc(doc(db, 'users', customUserId), {
            uid: user.uid,
            customId: customUserId,
            email: user.email,
            fullName: additionalData.firstName || '',
            role: 'user'
          });
        } catch (firestoreError) {
          console.warn('Firestore user doc creation failed:', firestoreError);
        }

      return user;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  // 🔹 LOGIN
  async function login(email, password) {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      return user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // 🔹 GOOGLE SIGN-IN
    async function googleSignIn() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        const [firstName] = (user.displayName || '').split(' ');
          try {
            await setDoc(userRef, {
              uid: user.uid,
              email: user.email || '',
              fullName: firstName || '',
              role: 'user'
            });
          } catch (firestoreError) {
            console.warn('Firestore user doc creation failed (Google):', firestoreError);
          }
      }

      return user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }

  // 🔹 LOGOUT
  async function logout() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // 🔹 UPDATE USER PROFILE
  async function updateUserProfile(updates) {
    try {
      if (currentUser) {
        await updateProfile(currentUser, updates);
        await setDoc(doc(db, 'users', currentUser.uid), updates, { merge: true });
      }
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  // 🔹 GET USER DATA
  async function getUserData() {
    try {
      if (!currentUser) return null;
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      console.error('Get user data error:', error);
      return null;
    }
  }

  // 🔹 AUTH STATE LISTENER
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const finalRole = data.role || (ADMIN_EMAILS.includes(user.email) ? 'admin' : 'user');
          setRole(finalRole);
        } else {
          const finalRole = ADMIN_EMAILS.includes(user.email) ? 'admin' : 'user';
          setRole(finalRole);
        }
      } catch (error) {
        console.warn('Failed to fetch role:', error);
        const finalRole = ADMIN_EMAILS.includes(user.email) ? 'admin' : 'user';
        setRole(finalRole);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    role,
    signup,
    login,
    logout,
    googleSignIn,
    updateUserProfile,
    getUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
